import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import LeadCard from "@/components/LeadCard";
import SafeAvatar from "@/components/SafeAvatar";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Crown, Coins, Filter, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";

export default function ProHome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [pros, setPros] = useState([]);
  const [filter, setFilter] = useState("all"); // all | my-trade | my-city
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filter === "my-trade" && user.trade) params.trade = user.trade;
    if (filter === "my-city" && user.city) params.city = user.city;
    const [{ data: l }, { data: p }] = await Promise.all([
      api.get("/leads/feed", { params }),
      api.get("/users/recent", { params: { limit: 10 } }),
    ]);
    setLeads(l); setPros(p);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <AppShell
      role="pro"
      title={`Bonjour ${user?.name?.split(" ")[0] || ""}`}
      right={
        <div className="flex items-center gap-2">
          {user?.subscription_active ? (
            <Badge className="bg-brand-light text-brand hover:bg-brand-light rounded-full" data-testid="badge-premium">
              <Crown className="w-3 h-3 mr-1" /> Premium
            </Badge>
          ) : (
            <button onClick={() => nav("/pro/profile")} className="flex items-center gap-1.5 bg-pro/10 text-pro px-3 py-1.5 rounded-full text-xs font-semibold tap-scale" data-testid="tokens-pill">
              <Coins className="w-3.5 h-3.5" /> {user?.tokens || 0}
            </button>
          )}
        </div>
      }
    >
      {/* Hero promo */}
      {!user?.subscription_active && (
        <button onClick={() => nav("/pro/profile")} className="w-full bg-pro text-white rounded-2xl p-4 mb-4 text-left tap-scale" data-testid="upsell-premium-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60">Premium</div>
              <div className="font-heading font-bold text-lg">Leads illimités + IA Pro</div>
              <div className="text-xs text-white/70 mt-1">9,99€/mois · Priorité sur tous les leads</div>
            </div>
            <Crown className="w-8 h-8 text-brand" />
          </div>
        </button>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto scroll-hide -mx-1 px-1">
        {[
          { k: "all", label: "Tous les leads" },
          { k: "my-trade", label: `Mon métier` },
          { k: "my-city", label: user?.city || "Ma ville" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`filter-${f.k}`}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold tap-scale border ${
              filter === f.k ? "bg-ink text-white border-ink" : "bg-white text-slate2 border-border"
            }`}
          >
            {f.label}
          </button>
        ))}
        <button className="shrink-0 ml-auto p-2 bg-white rounded-full border border-border" aria-label="filters"><Filter className="w-4 h-4 text-slate2" /></button>
      </div>

      {/* Recent signups widget */}
      {pros.length > 0 && (
        <section className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-heading font-bold text-ink">Nouveaux inscrits</h3>
            <span className="text-[10px] uppercase tracking-widest text-slate2">en direct</span>
          </div>
          <div className="flex gap-2.5 overflow-x-auto scroll-hide -mx-5 px-5">
            {pros.map((p) => (
              <div key={p.id} className="shrink-0 w-20 text-center" data-testid={`recent-user-${p.id}`}>
                <div className="relative">
                  <SafeAvatar src={p.avatar} role={p.role} name={p.name} className="w-16 h-16 rounded-2xl object-cover border-2 border-white shadow" />
                  <span className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${p.role === "pro" ? "bg-pro" : "bg-brand"}`} />
                </div>
                <div className="text-[11px] font-semibold text-ink mt-1.5 truncate">{p.name}</div>
                <div className="text-[10px] text-slate2 truncate">{p.trade_name || (p.role === "particulier" ? "Particulier" : "")}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Leads feed */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-heading font-bold text-ink">Demandes de contact</h3>
          <span className="text-[10px] uppercase tracking-widest text-slate2">{leads.length} dispos</span>
        </div>
        <div className="space-y-3">
          {loading && <div className="text-center py-10 text-slate2 text-sm">Chargement…</div>}
          {!loading && leads.length === 0 && (
            <div className="bg-white rounded-2xl border border-border p-6 text-center text-sm text-slate2">
              Aucune demande pour ce filtre.
            </div>
          )}
          {leads.map((L) => (
            <LeadCard key={L.id} lead={L} mode="pro" onUnlocked={(nl) => setLeads((arr) => arr.map((x) => x.id === nl.id ? { ...x, ...nl, is_unlocked: true, trade_name: x.trade_name } : x))} />
          ))}
        </div>
      </section>

      {!user?.subscription_active && (
        <button onClick={() => nav("/pro/ai")} className="w-full mt-6 bg-brand-light border border-brand/30 rounded-2xl p-4 text-left tap-scale" data-testid="ai-promo">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-brand" />
            <div>
              <div className="font-heading font-bold text-ink">IA Pro</div>
              <div className="text-xs text-slate2">Conseils pour booster votre activité (Premium)</div>
            </div>
          </div>
        </button>
      )}
    </AppShell>
  );
}
